import React, {FC} from 'react';
import classNames from 'classnames';
import Button from 'components/Button';
import {Variant} from 'components/Button/variants';
import {HubSpotForm} from 'components/ContentPagesComponents/HubSpotForm';
import LaptopSizeWrapper, {Circle} from 'components/ContentPagesComponents/LaptopSizeWrapper';
import {Markdown} from 'components/Markdown';
import OmniButton from 'components/OmniButton';
import {BG} from 'constants/index';
import {navigate} from 'gatsby';
import {useToggle} from 'hooks/useToggle';

import * as styles from './styles.module.scss';

type FormButtonProps = {
	actionBtnText?: string;
	actionBtnLink?: string;
	actionBtnForm?: string;
	variant: Variant;
	className?: string;
}

const FormButton: FC<FormButtonProps> = ({
	actionBtnText,
	actionBtnLink,
	actionBtnForm,
	variant,
	className,
}) => {
	const {open: show, toggle: toggleForm} = useToggle(false);

	if (actionBtnLink) {
		return (
			<Button
				variant={variant}
				className={className}
				onClick={() => navigate(actionBtnLink)}>
				{actionBtnText}
			</Button>
		);
	}
	if (actionBtnForm) {
		const Form = HubSpotForm(actionBtnForm);
		return (
			<>
				<Button
					variant={variant}
					className={className}
					onClick={toggleForm}>
					{actionBtnText}
				</Button>
				{show && (
					<Form show={show} onClose={toggleForm} />
				)}
			</>
		);
	}
	return null;
};

export interface TextOnlyProps {
	header?: string;
	circle?: Circle,
	body?: string;
	bg?: BG
	actionBtnText?: string;
	actionBtnLink?: string;
	actionBtnForm?: string;
	actionBtnConfig?: string;
}

const trimLines = (text: string) => (
	text
		.split('\n')
		.map((line) => line.trim())
		.join('\n')
);

export const TextOnly: FC<TextOnlyProps> = ({
	header,
	circle = Circle.secondary,
	body,
	bg,
	actionBtnText,
	actionBtnLink,
	actionBtnForm,
	actionBtnConfig,
}) => {
	const mdHeader = trimLines(header || '');
	const mdBody = trimLines(body || '');
	const background = bg ?? BG.light;
	return (
		<LaptopSizeWrapper bg={background} circle={circle}>
			<div className={classNames({
				[styles.block]: true,
				[styles.inverted]: bg === BG.dark,
			})}>
				{mdHeader && (
					<div>
						<Markdown className={styles.header} markdown={mdHeader} />
					</div>
				)}
				{mdBody && (
					<div>
						<Markdown className={styles.body} markdown={mdBody} />
					</div>
				)}
				{actionBtnConfig ? (
					<div className={styles.buttonWrapper}>
						<OmniButton
							variant='primary'
							className={styles.button}
							actionBtnText={actionBtnText}
							actionBtnConfig={actionBtnConfig}
						/>
					</div>
				) : (
					actionBtnText && (actionBtnForm || actionBtnLink) && (
						<div className={styles.buttonWrapper}>
							<FormButton
								actionBtnText={actionBtnText}
								actionBtnLink={actionBtnLink}
								actionBtnForm={actionBtnForm}
								variant={background === BG.dark ? 'light' : 'primary'}
								className={styles.button}
							/>
						</div>
					))}
			</div>
		</LaptopSizeWrapper>
	);
};

export default TextOnly;
